import { useState, useEffect, useRef } from "react"
import api from "@/services/api"
import type { BdrNewLead } from "./types"

export function formatBdrLeadPhone(value: string) {
  let raw = value.replace(/\D/g, "")
  if (raw.length > 11) raw = raw.slice(0, 11)
  let masked = raw
  if (raw.length > 10) masked = raw.replace(/^(\d\d)(\d{5})(\d{4}).*/, "($1) $2-$3")
  else if (raw.length > 5) masked = raw.replace(/^(\d\d)(\d{4})(\d{0,4}).*/, "($1) $2-$3")
  else if (raw.length > 2) masked = raw.replace(/^(\d\d)(\d{0,5}).*/, "($1) $2")
  else if (raw.length > 0) masked = raw.replace(/^(\d*)/, "($1")
  return masked
}

export function useBdrLeadFormCompany(
  newLead: BdrNewLead,
  setNewLead: (lead: BdrNewLead | ((prev: BdrNewLead) => BdrNewLead)) => void,
) {
  const [servicoInput, setServicoInput] = useState('')

  const [companySearch, setCompanySearch] = useState(newLead.empresa || '')
  const [isOpen, setIsOpen] = useState(false)
  const [options, setOptions] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [ocupacao, setOcupacao] = useState<{ tipo: string; responsavel: string; label: string } | null>(null)
  
  // Existing Contacts States
  const [contatosEmpresa, setContatosEmpresa] = useState<any[]>([])

  const wrapperRef = useRef<HTMLDivElement>(null)

  // Sync initial empresa name
  useEffect(() => {
    if (newLead.empresa !== undefined && newLead.empresa !== companySearch) {
      setCompanySearch(newLead.empresa)
    }
  }, [newLead.empresa])

  // Click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounced search for companies
  useEffect(() => {
    if (!isOpen || !companySearch || companySearch.trim().length < 1) {
      setOptions([])
      return
    }

    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const resp = await api.get(`/empresas/v2-search?q=${encodeURIComponent(companySearch.trim())}`)
        setOptions(resp.data?.data || [])
      } catch (err) {
        console.error('Error searching companies:', err)
        setOptions([])
      } finally {
        setLoading(false)
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [companySearch, isOpen])

  // Fetch company contacts when empresaId changes
  useEffect(() => {
    if (!newLead.empresaId) {
      setContatosEmpresa([])
      return
    }
    api.get(`/empresas/${newLead.empresaId}`)
      .then(resp => {
        const contatos = resp.data?.contatos || []
        setContatosEmpresa(contatos)
      })
      .catch(err => {
        console.error('Error loading company contacts:', err)
        setContatosEmpresa([])
      })
  }, [newLead.empresaId])

  function updateField(field: string, value: any) {
    setNewLead((prev) => ({ ...prev, [field]: value }))
  }

  // Format Phone Number
  function formatPhone(value: string) {
    let raw = value.replace(/\D/g, '')
    if (raw.length > 11) raw = raw.slice(0, 11)
    
    let masked = raw
    if (raw.length > 10) {
      masked = raw.replace(/^(\d\d)(\d{5})(\d{4}).*/, '($1) $2-$3')
    } else if (raw.length > 5) {
      masked = raw.replace(/^(\d\d)(\d{4})(\d{0,4}).*/, '($1) $2-$3')
    } else if (raw.length > 2) {
      masked = raw.replace(/^(\d\d)(\d{0,5}).*/, '($1) $2')
    } else if (raw.length > 0) {
      masked = raw.replace(/^(\d*)/, '($1')
    }
    return masked
  }

  function handleSelectCompany(emp: any) {
    setCompanySearch(emp.nome)
    setIsOpen(false)
    setOcupacao(emp.ocupacao || null)
    setNewLead((prev) => ({
      ...prev,
      empresa: emp.nome,
      empresaId: emp.id,
      site: emp.site || prev.site || '',
      segmento: emp.segmento || prev.segmento || '',
      plataforma: emp.plataforma || prev.plataforma || '',
      faturamento: emp.faturamento || prev.faturamento || '',
      cnpj: emp.cnpj || prev.cnpj || '',
    }))
  }

  function handleSelectContact(contact: any) {
    setNewLead((prev) => ({
      ...prev,
      contato: contact.nome || prev.contato,
      cargo: contact.cargo || prev.cargo || '',
      telefone: contact.telefone ? formatPhone(contact.telefone) : prev.telefone,
      email: contact.email || prev.email || '',
      decisor: contact.decisor !== undefined ? contact.decisor : prev.decisor,
    }))
  }

  function addServico() {
    const tag = servicoInput.trim()
    if (!tag) return
    const current = newLead.servicos || []
    if (current.includes(tag)) return
    setNewLead((prev) => ({ ...prev, servicos: [...current, tag] }))
    setServicoInput('')
  }

  function removeServico(tag: string) {
    setNewLead((prev) => ({
      ...prev,
      servicos: (prev.servicos || []).filter(s => s !== tag),
    }))
  }

  function handleServicoKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      addServico()
    }
  }

  return {
    servicoInput, setServicoInput, companySearch, setCompanySearch, isOpen, setIsOpen,
    options, loading, ocupacao, setOcupacao, contatosEmpresa, wrapperRef, updateField,
    handleSelectCompany, handleSelectContact, addServico, removeServico, handleServicoKeyDown,
    formatPhone,
  }
}
